// ConfirmModal — a small reusable confirmation dialog: toned icon tile, title,
// a description paragraph, and Cancel + confirm buttons. Built on Modal.
//
// `children` adds body content BELOW the description paragraph (the description
// itself renders inside a <p>, which cannot legally hold a form control), and
// `confirmDisabled` gates the confirm button — together they cover
// type-to-confirm dialogs like the project Danger Zone's delete.

import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Modal, { type ModalTone } from './Modal';
import Button, { type ButtonVariant } from './Button';

export interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  description: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** Icon-tile tint (also the natural pairing for the confirm intent). */
  tone?: ModalTone;
  /** Kebab-case Icon name for the header tile. */
  icon?: string;
  /** Confirm button style. Default 'primary'. */
  confirmVariant?: ButtonVariant;
  /** Kebab-case Icon name on the confirm button — the design pairs it with the
   *  header tile's icon so the action reads the same in both places. */
  confirmIcon?: string;
  loading?: boolean;
  /** Extra body content rendered BELOW the description paragraph — e.g. a
   *  type-to-confirm input. Kept outside the <p>, which cannot hold a form
   *  control. */
  children?: ReactNode;
  /** Blocks the confirm button while a precondition isn't met (type-to-confirm). */
  confirmDisabled?: boolean;
}

const ConfirmModal = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  cancelLabel,
  tone = 'brand',
  icon,
  confirmVariant = 'primary',
  confirmIcon,
  loading = false,
  children,
  confirmDisabled = false,
}: ConfirmModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onClose={onClose}
      size='sm'
      tone={tone}
      icon={icon}
      title={title}
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={loading}>
            {cancelLabel ?? t('button_cancel')}
          </Button>
          <Button
            variant={confirmVariant}
            iconStart={confirmIcon}
            onClick={onConfirm}
            loading={loading}
            disabled={confirmDisabled}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className='confirm-modal__body'>{description}</p>
      {children}
    </Modal>
  );
};

export default ConfirmModal;
